import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatAmount, methodLabel } from "../../lib/format";
import type { PricingPlan } from "../../lib/types";
import { ErrorNotice, PageHeader, Sheet, SheetHeader, Skeleton, Table, Td, Th, Tr } from "../../components/ui";

export function Pricing() {
  const plan = useQuery({
    queryKey: ["pricing"],
    queryFn: () => api.get<PricingPlan>("/api/dashboard/pricing"),
  });

  if (plan.isLoading) {
    return (
      <Sheet>
        <Skeleton rows={6} />
      </Sheet>
    );
  }
  if (plan.error) return <ErrorNotice error={plan.error} />;

  const data = plan.data!;

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Your fee schedule"
        description="Exactly what PayFlow charges on each transaction. The fee is applied when a payment succeeds — never on one that fails."
      />

      <Sheet className="settle settle-1 mb-6 overflow-hidden">
        <SheetHeader
          title={data.name}
          description={data.description}
          actions={
            <span className="rounded-full bg-paper-sunk px-2.5 py-1 text-[11.5px] text-ink-muted">
              {data.fee_bearer === "customer" ? "Added to the customer's total" : "Deducted from settlement"}
            </span>
          }
        />

        <Table>
          <thead>
            <tr>
              <Th>Applies to</Th>
              <Th>Fee</Th>
              <Th align="right">Minimum</Th>
              <Th align="right">Capped at</Th>
            </tr>
          </thead>
          <tbody>
            {/* The most specific rules first, which is the order they are applied in. */}
            {[...data.rules]
              .sort((a, b) => scope(b) - scope(a))
              .map((rule) => (
                <Tr key={rule.id}>
                  <Td>
                    {rule.method ? (
                      <span className="text-ink">{methodLabel(rule.method)}</span>
                    ) : (
                      <span className="text-ink-muted">All payment methods</span>
                    )}
                    {rule.currency && (
                      <span className="ml-1.5 font-mono text-[11.5px] text-ink-faint">
                        {rule.currency} only
                      </span>
                    )}
                  </Td>
                  <Td className="text-ink">{feeDescription(rule)}</Td>
                  <Td align="right" mono>
                    {rule.min_fee_minor > 0 ? formatAmount(rule.min_fee_minor) : "—"}
                  </Td>
                  <Td align="right" mono>
                    {rule.max_fee_minor > 0 ? formatAmount(rule.max_fee_minor) : "—"}
                  </Td>
                </Tr>
              ))}
          </tbody>
        </Table>
      </Sheet>

      <div className="grid gap-6 lg:grid-cols-2">
        <Sheet className="settle settle-2 overflow-hidden">
          <SheetHeader title="Worked example" description="A USD 100.00 payment" />
          <WorkedExample plan={data} />
        </Sheet>

        <Sheet className="settle settle-3 overflow-hidden">
          <SheetHeader title="Good to know" />
          <ul className="space-y-3 px-5 py-4 text-[13px] leading-relaxed text-ink-muted">
            <li className="flex gap-2.5">
              <span className="mt-[9px] h-px w-3 shrink-0 bg-rule-strong" aria-hidden="true" />
              Where several rules could apply, the most specific one wins: a rule naming
              both a method and a currency beats one naming only a method.
            </li>
            <li className="flex gap-2.5">
              <span className="mt-[9px] h-px w-3 shrink-0 bg-rule-strong" aria-hidden="true" />
              The bank's own fee is separate from ours and is shown on every payment, so
              you can always see the full cost of a transaction.
            </li>
            <li className="flex gap-2.5">
              <span className="mt-[9px] h-px w-3 shrink-0 bg-rule-strong" aria-hidden="true" />
              Fees are calculated in whole cents and rounded half-up, once, at the moment
              the payment is created. The calculation is stored with the payment.
            </li>
          </ul>
        </Sheet>
      </div>
    </>
  );
}

function scope(rule: PricingPlan["rules"][number]): number {
  return (rule.method ? 2 : 0) + (rule.currency ? 1 : 0);
}

function feeDescription(rule: PricingPlan["rules"][number]): string {
  const flat = formatAmount(rule.flat_minor);
  switch (rule.model) {
    case "flat":
      return `${flat} per transaction`;
    case "percentage":
      return `${rule.percentage}% of the amount`;
    default:
      return `${flat} + ${rule.percentage}% of the amount`;
  }
}

/** WorkedExample turns the schedule into the one number a merchant actually wants:
 *  what they keep on a hundred dollars. */
function WorkedExample({ plan }: { plan: PricingPlan }) {
  const amount = 10_000;
  const rule =
    [...plan.rules].sort((a, b) => scope(b) - scope(a)).find((r) => !r.method && !r.currency) ??
    plan.rules[0];

  if (!rule) return <p className="px-5 py-6 text-[13px] text-ink-muted">No rules configured.</p>;

  const flat = rule.model === "percentage" ? 0 : rule.flat_minor;
  const percent = rule.model === "flat" ? 0 : Math.round((amount * rule.percentage_bps) / 10_000);
  let fee = flat + percent;
  let note = "";
  if (rule.min_fee_minor > 0 && fee < rule.min_fee_minor) {
    fee = rule.min_fee_minor;
    note = "raised to the minimum";
  }
  if (rule.max_fee_minor > 0 && fee > rule.max_fee_minor) {
    fee = rule.max_fee_minor;
    note = "reduced to the cap";
  }

  const customerBears = plan.fee_bearer === "customer";

  return (
    <dl className="divide-y divide-rule text-[13px]">
      <Row label="You ask to collect" value={formatAmount(amount)} />
      {flat > 0 && <Row label="Fixed component" value={formatAmount(flat)} muted />}
      {percent > 0 && (
        <Row label={`${rule.percentage}% of the amount`} value={formatAmount(percent)} muted />
      )}
      <Row
        label={`PayFlow fee${note ? ` (${note})` : ""}`}
        value={`−${formatAmount(fee)}`}
        accent="brass"
      />
      <Row
        label={customerBears ? "Customer is charged" : "Customer is charged"}
        value={formatAmount(customerBears ? amount + fee : amount)}
      />
      <Row
        label="You receive, before the bank's fee"
        value={formatAmount(customerBears ? amount : amount - fee)}
        accent="verdigris"
        strong
      />
    </dl>
  );
}

function Row({
  label,
  value,
  muted,
  strong,
  accent,
}: {
  label: string;
  value: string;
  muted?: boolean;
  strong?: boolean;
  accent?: "brass" | "verdigris";
}) {
  const colour =
    accent === "brass"
      ? "text-brass"
      : accent === "verdigris"
        ? "text-verdigris"
        : "text-ink";
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-2.5">
      <dt className={muted ? "text-ink-faint" : "text-ink-muted"}>{label}</dt>
      <dd className={`font-mono tabular ${strong ? "text-[15px] font-medium" : ""} ${colour}`}>{value}</dd>
    </div>
  );
}
